<script setup lang="ts">
import FeatureFlag from '@/components/FeatureFlag.vue';
import { Constants } from '@/constants';
import { useCommunityStore } from '@/stores';
import { useRoleCheck } from '@/composables/useRoleCheck';
import { computed, ref } from 'vue';

// CONST
const isDrawerCollapsedRef = ref(false);

// STORE
const communityStore = useCommunityStore();

// PROPS
const props = defineProps<{
  role: number;
  isDrawerCollapsed: boolean;
}>();

// Superadmin gate for the Customers menu item (same composable the rest of the app uses).
const { isUserAuthorized: isSuperadmin } = useRoleCheck({ requiresSuperadmin: true });

// EMITS
const emit = defineEmits<{
  (e: 'toggle', isDrawerCollapsed: boolean): void;
}>();

// COMPUTED
const community = computed(() => {
  return communityStore.community;
});

// FUNCTIONS
function isAdmin() {
  return props.role === Constants.User.Role.ADMIN;
}

function toggleLeftDrawer() {
  emit('toggle', isDrawerCollapsedRef.value);
}
</script>

<template>
  <q-list padding dark class="left-menu">
    <q-item-label header>{{ $t('menu.community-management') }}</q-item-label>
    <q-btn @click="toggleLeftDrawer" flat color="secondary" v-if="props.isDrawerCollapsed">
      <q-icon name="chevron_right" class="cursor-pointer" color="white" />
    </q-btn>
    <q-item to="/" active-class="q-item-no-link-highlighting">
      <q-item-section avatar>
        <q-icon color="white" name="dashboard" />
      </q-item-section>
      <q-item-section>{{ $t('menu.dashboard') }}</q-item-section>
    </q-item>
    <q-item to="/consultations" active-class="q-item-no-link-highlighting">
      <q-item-section avatar>
        <q-icon color="white" name="forum" />
      </q-item-section>
      <q-item-section>{{ $t('menu.consultations') }}</q-item-section>
    </q-item>
    <q-item to="/projects" active-class="q-item-no-link-highlighting">
      <q-item-section avatar>
        <q-icon color="white" name="folder" />
      </q-item-section>
      <q-item-section>{{ $t('menu.projects') }}</q-item-section>
    </q-item>
    <feature-flag v-if="isAdmin()" feature="pages">
      <q-item :to="{ name: 'pages_list' }" active-class="q-item-no-link-highlighting">
        <q-item-section avatar>
          <q-icon color="white" name="article" />
        </q-item-section>
        <q-item-section>{{ $t('menu.pages') }}</q-item-section>
      </q-item>
    </feature-flag>
    <div v-if="isAdmin()">
      <q-item to="/mailing-campaign" active-class="q-item-no-link-highlighting">
        <q-item-section avatar>
          <q-icon color="white" name="email" />
        </q-item-section>
        <q-item-section>{{ $t('menu.mailingCampaign') }}</q-item-section>
      </q-item>
      <q-item :to="{ name: 'community_details' }" active-class="q-item-no-link-highlighting">
        <q-item-section avatar>
          <q-icon color="white" name="settings" />
        </q-item-section>
        <q-item-section>{{ $t('menu.settings') }}</q-item-section>
      </q-item>
      <q-separator spaced inset color="white" />
      <q-item-label header>{{ $t('menu.admin-management') }}</q-item-label>
      <q-item to="/communities" active-class="q-item-no-link-highlighting">
        <q-item-section avatar>
          <q-icon color="white" name="format_list_bulleted" />
        </q-item-section>
        <q-item-section>{{ $t('menu.communities') }}</q-item-section>
      </q-item>
      <q-item v-if="isSuperadmin" to="/customers" active-class="q-item-no-link-highlighting">
        <q-item-section avatar>
          <q-icon color="white" name="insights" />
        </q-item-section>
        <q-item-section>{{ $t('menu.customers') }}</q-item-section>
      </q-item>
    </div>
    <!-- <q-item v-if="isSuperadmin" to="/comment-moderation" active-class="q-item-no-link-highlighting">
            <q-item-section avatar>
                <q-icon color="white" name="o_comment"/>
            </q-item-section>
            <q-item-section>{{ $t('menu.commentModeration') }}</q-item-section>
        </q-item> -->
  </q-list>
  <div class="help-center">
    <q-list padding dark class="left-menu">
      <q-item :href="$t('menu.helpCenter.url')" target="_blank">
        <q-item-section avatar>
          <q-icon color="white" name="help" />
        </q-item-section>
        <q-item-section>{{ $t('menu.helpCenter.title') }}</q-item-section>
      </q-item>
    </q-list>
  </div>
</template>
<style lang="scss">
.help-center {
  align-content: end;
  font-size: 14px;
  height: 100vh;
  margin-bottom: 20px;
  i {
    font-size: 18px;
  }
  .q-item {
    .q-focus-helper {
      display: none;
    }
  }
  .q-list {
    padding: 0;
    &:hover {
      .q-item__section--main {
        text-decoration: underline;
      }
    }
    & > .q-item {
      min-height: 32px;
    }
  }
}
.left-menu {
  .q-item {
    display: flex;
    flex-direction: row;
    .q-item__section--side {
      min-width: 0;
    }
  }
}
</style>
